// src/components/Popups/CreateRezervation.jsx
import React, {
   useDeferredValue,
   useEffect,
   useContext,
   useMemo,
   useRef,
   useState,
   useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import AlertPills from "../../Utils/AlertPills";
import UIIcon from "../../Common/UIIcon";

import {
   createReservationsForUser,
   getUserReservations,
   deleteReservation,
} from "../../../api/reservationsService";

import { fetchStudents } from "../../../store/studentsSlice";
import { fetchInstructors } from "../../../store/instructorsSlice";
import {
   fetchReservationsDelta,
   addReservationLocal,
   removeReservationLocal,
} from "../../../store/reservationsSlice";
import {
   triggerCalendarRefresh,
   scheduleCalendarRefresh,
} from "../../Utils/calendarBus";
import { enqueueCalendarMutation } from "../../../calendar/sync/mutationQueue";
import { formatCalendarCreateError } from "../../Utils/calendarCreateErrors";
import {
   CALENDAR_EVENT_COLOR_GRID_COLUMNS,
   CALENDAR_EVENT_COLOR_HINTS,
   CALENDAR_EVENT_COLOR_LABELS,
   CALENDAR_EVENT_COLOR_TOKENS,
   calendarEventColorSwatchStyle,
} from "../../../constants/calendarEventColors";
import { getCalendarStore } from "../../../calendar/master/store/calendarStoreRegistry";
import {
   applyOptimisticUpsert,
   applyOptimisticRemove,
   applyOptimisticReplaceId,
   completePendingCommand,
   compactPatchBusAfterIdPromotion,
} from "../../../calendar/master/mutations/calendarMutationSync";
import { buildOptimisticSlotVisualItem } from "../../../calendar/interaction/buildOptimisticSlotVisualItem";
import {
   hasCalendarSlotInstructorMismatch,
   resolveCalendarSlotKeyForCreate,
} from "../../../calendar/interaction/resolveCalendarSlotKeyForCreate";
import { resolveCreatedReservationId } from "../../../calendar/interaction/resolveCreatedReservationId";
import { buildCreateUndoEntry } from "../../../calendar/master/mutations/commandUndoEntries";
import { resolveBlackoutLocalKey } from "../../../calendar/ultra/blackoutLocalKey";
import {
   blackoutDebug,
   explainBlackoutDateTime,
} from "../../../calendar/debug/blackoutDebug";
import { OPTIMISTIC_BLACKOUT_REVALIDATE_DELAY_MS } from "../../../calendar/interaction/constants";

import apiClientService from "../../../api/ApiClientService";
import {
   getInstructorBlackouts,
   addInstructorBlackout,
   deleteInstructorBlackout,
} from "../../../api/instructorsService";

import {
   closePopup as closePopupStore,
   closeSubPopup as closeSubPopupStore,
} from "../../Common/popupUIStore";
import { fetchCars } from "../../../store/carsSlice";
import { fetchInstructorsGroups } from "../../../store/instructorsGroupSlice";
import { UserContext } from "../../../UserContext";
import {
   canOwnCalendarReservations,
   isInstructorSelfProfileMode,
} from "../../../constants/instructors";
import {
   MOLDOVA_TZ,
   BUSY_KEYS_MODE,
   getFmt,
   hhmmInTZ,
   toUtcIsoFromMoldova,
   isoForDbMatchLocalHour,
   busyLocalKeyFromStored,
} from "../../../utils/moldovaTime";

const SEARCH_RESULTS_LIMIT = 10;
const EMPTY_USERS = [];
const CREATE_POPUP_IS_LOW_SPEC_DEVICE =
   typeof navigator !== "undefined" &&
   ((Number(navigator.deviceMemory) > 0 &&
      Number(navigator.deviceMemory) <= 4) ||
      (Number(navigator.hardwareConcurrency) > 0 &&
         Number(navigator.hardwareConcurrency) <= 4));

function isCreateReservationDebugEnabled() {
   if (typeof window === "undefined") return false;
   try {
      return (
         window.__HTTP_DEBUG === true ||
         window.__CREATE_RESERVATION_DEBUG === true ||
         localStorage.getItem("__HTTP_DEBUG") === "1" ||
         localStorage.getItem("__CREATE_RESERVATION_DEBUG") === "1"
      );
   } catch {
      return (
         window.__HTTP_DEBUG === true ||
         window.__CREATE_RESERVATION_DEBUG === true
      );
   }
}

function debugCreateReservation(label, payload) {
   if (!isCreateReservationDebugEnabled()) return;
}

let cachedStudentLookupSource = null;
let cachedStudentLookupValue = {
   searchIndex: EMPTY_USERS,
   exactPhoneDigits: new Map(),
};

function hasUserRole(u) {
   const role = String(u?.role ?? u?.Role ?? u?.userRole ?? "").toUpperCase();
   if (role === "USER") return true;
   const roles = Array.isArray(u?.roles)
      ? u.roles.map((r) => String(r).toUpperCase())
      : [];
   return roles.includes("USER");
}

function normalizeCreateSector(raw, fallback = "") {
   const s = String(raw || "")
      .trim()
      .toLowerCase();
   if (!s) return String(fallback || "").trim();
   if (s.includes("bot")) return "Botanica";
   if (s.includes("cio")) return "Ciocana";
   if (s.includes("bui")) return "Buiucani";
   return String(raw || "").trim() || String(fallback || "").trim();
}

function normalizeCreateGearbox(raw, fallback = "") {
   const s = String(raw || "")
      .trim()
      .toLowerCase();
   if (!s) return String(fallback || "").trim();
   if (s.includes("auto")) return "Automat";
   if (s.includes("man")) return "Manual";
   return String(raw || "").trim() || String(fallback || "").trim();
}

function normalizeInstructorIdForCreate(value) {
   if (value == null) return "";
   const raw = String(value).trim();
   if (!raw || raw === "__unknown" || raw.startsWith("__")) return "";
   const n = Number(raw);
   if (!Number.isFinite(n) || n <= 0) return "";
   return String(Math.trunc(n));
}

function findInstructorGroupForCreate(
   instructorId,
   instructor,
   instructorsGroups,
) {
   const groups = Array.isArray(instructorsGroups) ? instructorsGroups : [];
   const idStr = normalizeInstructorIdForCreate(instructorId ?? instructor?.id);
   if (!idStr) return null;

   const directGroupIdCandidates = [
      instructor?.instructorsGroupId,
      instructor?.instructors_group_id,
      instructor?.groupId,
      instructor?.group_id,
      instructor?.group?.id,
   ]
      .map((value) => normalizeInstructorIdForCreate(value))
      .filter(Boolean);

   for (const groupId of directGroupIdCandidates) {
      const directGroup =
         groups.find((group) => String(group?.id ?? "") === groupId) || null;
      if (directGroup) return directGroup;
   }

   return (
      groups.find(
         (group) =>
            Array.isArray(group?.instructors) &&
            group.instructors.some(
               (entry) => String(entry?.id ?? entry ?? "") === idStr,
            ),
      ) || null
   );
}

function resolveSectorFromInstructorForCreate({
   instructorId,
   instructor,
   instructorsGroups,
   fallback = "",
}) {
   const directSector = normalizeCreateSector(
      instructor?.sector ??
         instructor?.sectorSlug ??
         instructor?.sectorName ??
         instructor?.location,
      "",
   );
   if (directSector) return directSector;

   const group = findInstructorGroupForCreate(
      instructorId,
      instructor,
      instructorsGroups,
   );
   const groupSector = normalizeCreateSector(
      group?.sector ?? group?.location,
      "",
   );
   if (groupSector) return groupSector;

   return normalizeCreateSector(fallback, "");
}

function resolveGearboxFromInstructorForCreate({
   instructorId,
   instructor,
   cars,
   fallback = "",
}) {
   const idStr = normalizeInstructorIdForCreate(instructorId ?? instructor?.id);
   const car =
      idStr && Array.isArray(cars)
         ? cars.find(
              (entry) =>
                 String(entry?.instructorId ?? entry?.instructor_id ?? "") ===
                 idStr,
           ) || null
         : null;

   return normalizeCreateGearbox(
      car?.gearbox ?? instructor?.gearbox ?? instructor?.car?.gearbox,
      fallback,
   );
}

function pickStudentPhoneValue(user) {
   return String(
      user?.phone ?? user?.phoneNumber ?? user?.mobile ?? user?.telefon ?? "",
   ).trim();
}

function buildPhoneLookupKeys(value) {
   const raw = onlyDigits(value);
   if (!raw) return [];

   const keys = [];
   const push = (candidate) => {
      const digits = onlyDigits(candidate);
      if (!digits || keys.includes(digits)) return;
      keys.push(digits);
   };

   const addCountryVariants = (digits) => {
      const normalized = onlyDigits(digits);
      if (!normalized.startsWith("373")) return;
      const local = normalized.slice(3);
      push(local);
      if (local.length === 9 && local.startsWith("0")) {
         push(local.slice(1));
      }
   };

   push(raw);

   const withoutIntlPrefix = raw.replace(/^00/, "");
   if (withoutIntlPrefix && withoutIntlPrefix !== raw) {
      push(withoutIntlPrefix);
   }

   addCountryVariants(raw);
   addCountryVariants(withoutIntlPrefix);

   if (raw.startsWith("0")) {
      push(raw.replace(/^0+/, ""));
   }
   if (withoutIntlPrefix && withoutIntlPrefix.startsWith("0")) {
      push(withoutIntlPrefix.replace(/^0+/, ""));
   }

   return keys;
}

function findStudentByPhoneDigits(exactPhoneDigits, value) {
   const map = exactPhoneDigits instanceof Map ? exactPhoneDigits : null;
   if (!map) return null;

   const lookupKeys = buildPhoneLookupKeys(value);
   for (const key of lookupKeys) {
      const hit = map.get(key);
      if (hit) return hit;
   }
   return null;
}

function matchesPhoneDigits(entry, value) {
   const typedDigits = onlyDigits(value);
   if (!typedDigits) return false;

   const entryKeys = Array.isArray(entry?.phoneLookupKeys)
      ? entry.phoneLookupKeys.filter(Boolean)
      : [];
   if (!entryKeys.length) return false;

   const typedKeys = buildPhoneLookupKeys(value);
   if (typedKeys.some((key) => entryKeys.includes(key))) return true;

   if (typedDigits.length < 4) return false;
   return entryKeys.some((key) => key.includes(typedDigits));
}

function isIOSLikeDevice() {
   if (typeof navigator === "undefined") return false;
   const ua = String(navigator.userAgent || "");
   const platform = String(navigator.platform || "");
   const maxTouchPoints = Number(navigator.maxTouchPoints || 0);
   return (
      /iPad|iPhone|iPod/i.test(ua) ||
      (platform === "MacIntel" && maxTouchPoints > 1)
   );
}

function shouldAutoFocusCreatePhoneInput() {
   if (typeof window === "undefined") return false;
   if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
      return false;
   if (window.matchMedia?.("(max-width: 860px)").matches) return false;
   if (CREATE_POPUP_IS_LOW_SPEC_DEVICE) return false;
   if (isIOSLikeDevice()) return false;
   return true;
}

function getCachedStudentLookup(list) {
   const src = Array.isArray(list) ? list : EMPTY_USERS;
   if (cachedStudentLookupSource === src) return cachedStudentLookupValue;

   const exactPhoneDigits = new Map();
   const searchIndex = [];

   for (const user of src) {
      if (!hasUserRole(user)) continue;
      const phone = pickStudentPhoneValue(user);
      const phoneLookupKeys = buildPhoneLookupKeys(phone);

      for (const key of phoneLookupKeys) {
         if (!exactPhoneDigits.has(key)) {
            exactPhoneDigits.set(key, user);
         }
      }

      searchIndex.push({
         user,
         phoneLookupKeys,
      });
   }

   cachedStudentLookupSource = src;
   cachedStudentLookupValue = {
      searchIndex,
      exactPhoneDigits,
   };
   return cachedStudentLookupValue;
}

function findStudentsForSearch(list, phoneDigits, limit = 20) {
   const src = Array.isArray(list) ? list : EMPTY_USERS;
   const typedDigits = onlyDigits(phoneDigits);
   if (!typedDigits) return [];

   const out = [];
   for (const s of src) {
      const user = s?.user || s;
      if (!hasUserRole(user)) continue;

      const matchPhoneDigits = matchesPhoneDigits(s, typedDigits);
      if (!matchPhoneDigits) continue;
      out.push(user);
      if (out.length >= limit) break;
   }
   return out;
}

const oreDisponibile = [
   { eticheta: "07:00", oraStart: "07:00" },
   { eticheta: "08:30", oraStart: "08:30" },
   { eticheta: "10:00", oraStart: "10:00" },
   { eticheta: "11:30", oraStart: "11:30" },
   { eticheta: "13:30", oraStart: "13:30" },
   { eticheta: "15:00", oraStart: "15:00" },
   { eticheta: "16:30", oraStart: "16:30" },
   { eticheta: "18:00", oraStart: "18:00" },
   { eticheta: "19:30", oraStart: "19:30" },
];

/* =========================
   ✅ ADD: Culori (aceeași paletă ca examen practic / ultra)
========================= */
const COLOR_TOKENS = CALENDAR_EVENT_COLOR_TOKENS;
const COLOR_LABEL = CALENDAR_EVENT_COLOR_LABELS;
const COLOR_HINTS = CALENDAR_EVENT_COLOR_HINTS;
const COLOR_GRID_STYLE = {
   gridTemplateColumns: `repeat(${CALENDAR_EVENT_COLOR_GRID_COLUMNS}, minmax(0, 1fr))`,
};

/* Helpers diverse */
const toUtcIsoFromLocal = toUtcIsoFromMoldova;

const onlyDigits = (s = "") => {
   const raw = s == null ? "" : String(s);
   const normalized =
      typeof raw.normalize === "function" ? raw.normalize("NFKC") : raw;
   return normalized.replace(/\D/g, "");
};

function normalizePhoneForStudentCreate(phoneValue) {
   const digits = onlyDigits(phoneValue);
   if (!digits) return "";
   const noLeadingZeros = digits.replace(/^0+/, "");
   return noLeadingZeros || digits;
}

function splitFullName(full = "") {
   const parts = full.trim().split(/\s+/).filter(Boolean);
   if (!parts.length) return { firstName: "", lastName: "" };
   if (parts.length === 1) return { firstName: parts[0], lastName: "" };
   return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function escapeRegExp(s) {
   return (s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightText(text, query) {
   if (!text) return "";
   if (!query) return text;

   const safe = escapeRegExp(query);
   if (!safe) return text;

   const parts = text.toString().split(new RegExp(`(${safe})`, "gi"));
   const qLower = (query || "").toLowerCase();
   return parts.map((part, i) =>
      part.toLowerCase() === qLower ? (
         <i key={i} className="highlight">
            {part}
         </i>
      ) : (
         part
      ),
   );
}

const formatDateRO = (iso) => {
   if (!iso) return "";
   const d = new Date(iso);
   const fmt = getFmt("ro-RO", MOLDOVA_TZ, "date-long").format(d);
   return fmt.replace(/\b([a-zăîâșț])/u, (m) => m.toUpperCase());
};

function getBlackoutDT(b) {
   if (!b) return null;
   if (typeof b === "string") return b;
   const t = String(b.type || b.Type || "").toUpperCase();
   if (t === "REPEAT")
      return b.startDateTime || b.dateTime || b.datetime || null;
   return b.dateTime || b.datetime || b.startTime || b.date || b.begin || null;
}

function pad2(n) {
   return String(n).padStart(2, "0");
}
function getMonthRangeYMD(dateObj) {
   const d = new Date(dateObj);
   const y = d.getFullYear();
   const m = d.getMonth();
   const lastDay = new Date(y, m + 1, 0).getDate();
   return {
      startDate: `${y}-${pad2(m + 1)}-01`,
      endDate: `${y}-${pad2(m + 1)}-${pad2(lastDay)}`,
   };
}

/** Wall-clock slot key from popup selection — same hour shown in calendar, no TZ shift. */
function selectedSlotLocalKey(selectedDate, selectedTime) {
   if (!(selectedDate instanceof Date) || !selectedTime?.oraStart) return "";
   const y = selectedDate.getFullYear();
   const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
   const d = String(selectedDate.getDate()).padStart(2, "0");
   const hmMatch = String(selectedTime.oraStart || "")
      .trim()
      .match(/^(\d{1,2}):(\d{2})$/);
   if (!hmMatch) return "";
   const hm = `${String(Number(hmMatch[1])).padStart(2, "0")}:${hmMatch[2]}`;
   return `${y}-${m}-${d}|${hm}`;
}

function blackoutIdFromRow(row) {
   return (
      row?.id ?? row?._id ?? row?.blackoutId ?? row?.blackout_id ?? null
   );
}

function matchBlackoutAtSlot(list, targetKey, gridHmSet = null) {
   let foundSingle = null;
   let foundRepeat = false;
   const key = String(targetKey || "").trim();
   if (!key) return { foundSingle, foundRepeat };
   // Use the full grid hour set when available. A single-hour validHmSet used to
   // false-match Instant "…T10:00:00.000Z" onto 10:00 via raw digits while the
   // calendar painted 13:00 (Moldova-from-UTC) — popup said Deblochează, cell not red.
   const validHmSet =
      gridHmSet instanceof Set && gridHmSet.size > 0 ? gridHmSet : null;

   for (const row of list || []) {
      const typeRaw = String(row?.type || row?.Type || "")
         .trim()
         .toUpperCase();
      const isRepeat =
         typeRaw === "REPEAT" ||
         typeRaw.includes("REPEAT") ||
         row?.repeatEveryDays != null ||
         row?.repeat_every_days != null;

      if (isRepeat) {
         if (repeatContainsSlotKey(row, key)) {
            foundRepeat = true;
         }
         continue;
      }

      // Missing/empty type is treated as SINGLE (same as calendar grid).
      const dt = getBlackoutDT(row);
      if (!dt) continue;
      if (resolveBlackoutLocalKey(dt, { validHmSet }) === key) {
         foundSingle = row;
         break;
      }
   }

   return { foundSingle, foundRepeat };
}

async function fetchInstructorBlackoutListForDate(instructorId, selectedDate) {
   const monthRange = getMonthRangeYMD(selectedDate);
   try {
      return await getInstructorBlackouts(instructorId, monthRange);
   } catch {
      return await getInstructorBlackouts(instructorId);
   }
}

async function unblockSingleBlackoutAtSlot({
   instructorId,
   selectedDate,
   selectedTime,
   knownBlackoutId = null,
}) {
   const targetKey = selectedSlotLocalKey(selectedDate, selectedTime);
   if (!targetKey) return { ok: false, targetKey: "" };

   if (knownBlackoutId != null) {
      await deleteInstructorBlackout(knownBlackoutId);
      return { ok: true, targetKey, removedId: knownBlackoutId };
   }

   const list = await fetchInstructorBlackoutListForDate(
      instructorId,
      selectedDate,
   );
   const { foundSingle } = matchBlackoutAtSlot(list, targetKey);
   const removedId = blackoutIdFromRow(foundSingle);
   if (removedId == null) return { ok: false, targetKey };

   await deleteInstructorBlackout(removedId);
   return { ok: true, targetKey, removedId };
}

function safeLocalDateFromYMD(ymd) {
   const m = String(ymd || "")
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})$/);
   if (!m) return null;
   const Y = +m[1],
      Mo = +m[2],
      D = +m[3];
   return new Date(Y, Mo - 1, D, 12, 0, 0, 0);
}

function reservationIdFromAny(r) {
   return r?.id ?? r?.reservationId ?? r?.reservation?.id ?? null;
}

function reservationInstructorIdFromAny(r) {
   return (
      r?.instructorId ??
      r?.instructor_id ??
      r?.instructor?.id ??
      r?.reservation?.instructorId ??
      r?.reservation?.instructor_id ??
      r?.reservation?.instructor?.id ??
      null
   );
}

function reservationUserIdFromAny(r) {
   return (
      r?.studentId ??
      r?.student_id ??
      r?.student?.id ??
      r?.userId ??
      r?.user_id ??
      r?.user?.id ??
      r?.reservation?.studentId ??
      r?.reservation?.student_id ??
      r?.reservation?.student?.id ??
      r?.reservation?.userId ??
      r?.reservation?.user_id ??
      r?.reservation?.user?.id ??
      null
   );
}

function reservationStartFromAny(r) {
   return (
      r?.startTime ??
      r?.start_time ??
      r?.start ??
      r?.dateTime ??
      r?.datetime ??
      r?.date ??
      r?.reservation?.startTime ??
      r?.reservation?.start_time ??
      r?.reservation?.start ??
      r?.reservation?.dateTime ??
      r?.reservation?.datetime ??
      r?.reservation?.date ??
      null
   );
}

function extractCreatedReservations(payload) {
   const out = [];
   const seen = new Set();

   const visit = (node, depth = 0) => {
      if (node == null || depth > 7) return;
      if (Array.isArray(node)) {
         for (const item of node) visit(item, depth + 1);
         return;
      }
      if (typeof node !== "object") return;

      const rid = reservationIdFromAny(node);
      const instructorId = reservationInstructorIdFromAny(node);
      const userId = reservationUserIdFromAny(node);
      const startRaw = reservationStartFromAny(node);

      const looksLikeReservation = !!(
         (rid != null &&
            (startRaw != null || userId != null || instructorId != null)) ||
         (startRaw != null && (userId != null || instructorId != null))
      );

      if (looksLikeReservation) {
         const key = `${String(rid ?? "")}|${String(startRaw ?? "")}|${String(
            userId ?? "",
         )}|${String(instructorId ?? "")}`;
         if (!seen.has(key)) {
            seen.add(key);
            out.push({
               id: rid != null ? Number(rid) : NaN,
               instructorId: instructorId != null ? Number(instructorId) : NaN,
               userId: userId != null ? Number(userId) : NaN,
               startRaw: startRaw != null ? String(startRaw) : "",
            });
         }
      }

      for (const value of Object.values(node)) {
         if (value && typeof value === "object") visit(value, depth + 1);
      }
   };

   visit(payload, 0);
   return out;
}

/** Match create API row by elev + oră. */
async function resolvePopupCreateConfirmation({
   createResult,
   studentIdNum,
   instructorIdNum,
   targetLocalKey,
   optimisticStartLocal,
   optimisticUser,
   sectorToSend,
   gearboxToSend,
   payloadPrivateMessage,
   payloadColorToken,
   payloadIsFavorite,
   payloadIsImportant,
}) {
   const createdResolution = resolveCreatedReservationId(createResult, {
      userId: studentIdNum,
      instructorId: instructorIdNum,
   });
   let confirmedId = createdResolution.confirmedId || "";

   if (!confirmedId) {
      const byTime = extractCreatedReservations(createResult).filter((row) => {
         if (!Number.isFinite(row?.id)) return false;
         if (
            !Number.isFinite(row?.userId) ||
            Number(row.userId) !== studentIdNum
         ) {
            return false;
         }
         if (
            Number.isFinite(instructorIdNum) &&
            Number.isFinite(Number(reservationInstructorIdFromAny(row))) &&
            Number(reservationInstructorIdFromAny(row)) !== Number(instructorIdNum)
         ) {
            return false;
         }
         if (!targetLocalKey || !row?.startRaw) return false;
         return busyLocalKeyFromStored(row.startRaw) === targetLocalKey;
      });
      const pick = byTime.reduce((best, row) => {
         if (!best) return row;
         return Number(row.id) > Number(best.id) ? row : best;
      }, null);
      if (pick) {
         confirmedId = String(pick.id);
      }
   }

   if (!confirmedId) {
      return { createdId: "", confirmedEvent: null, needsPostSync: true };
   }

   const confirmedEvent = {
      id: confirmedId,
      userId: studentIdNum,
      instructorId: instructorIdNum,
      startTime: optimisticStartLocal,
      sector: sectorToSend || null,
      gearbox:
         gearboxToSend.toLowerCase() === "automat" ? "Automat" : "Manual",
      privateMessage: payloadPrivateMessage,
      color: payloadColorToken,
      isFavorite: payloadIsFavorite,
      isImportant: payloadIsImportant,
      _optimistic: false,
      _optimisticPending: false,
      _localEchoPending: true,
      _localEchoUntil: Date.now() + 15000,
      ...(optimisticUser ? { user: optimisticUser } : {}),
   };

   return {
      createdId: confirmedId,
      confirmedEvent,
      needsPostSync: false,
   };
}

function repeatContainsSlotKey(b, targetKey) {
   const type = String(b?.type || b?.Type || "").toUpperCase();
   if (type !== "REPEAT") return false;

   const stepDays = Math.max(1, Number(b?.repeatEveryDays || 1));
   const first = b?.startDateTime || b?.dateTime || b?.datetime || null;
   const last = b?.endDateTime || first;
   if (!first || !last) return false;

   let cur = new Date(first).getTime();
   const lastMs = new Date(last).getTime();
   if (Number.isNaN(cur) || Number.isNaN(lastMs)) return false;

   const targetHm = String(targetKey.split("|")[1] || "").trim();
   const validHmSet = targetHm ? new Set([targetHm]) : null;

   while (cur <= lastMs) {
      const k = resolveBlackoutLocalKey(new Date(cur).toISOString(), {
         validHmSet,
      });
      if (k && k === targetKey) return true;
      cur += stepDays * 24 * 60 * 60 * 1000;
   }
   return false;
}

export default function CreateRezervation({
   initialStartTime,
   initialDate,
   initialTime,
   initialStudentId,
   initialInstructorId,
   initialSector = "Botanica",
   initialGearbox = "Manual",
   initialPrivateMessage = "",
   initialColorToken = "--black-t",
   initialIsFavorite = false,
   initialIsImportant = false,
   requireInstructorSelection = false,
   deriveFromSelectedInstructor = false,
   lockInstructorSelection = false,
   availableColorTokens = COLOR_TOKENS,
   showTicketFlags = true,
   showBlackoutAction = true,
   showColorControl = true,
   showPersonalNote = true,
   createMode = "",

   start,
   end,
   instructorId: instructorIdFromPayload,
   sector: sectorFromPayload,
   gearbox: gearboxFromPayload,

   draftSlotKey,
   calendarSlotKey: calendarSlotKeyFromPayload,
   calendarDayIndex: calendarDayIndexFromPayload,
   initialSlotIsBlocked = false,
   draftPresenceAlreadyStarted = false,
   onClose,
}) {
   const dispatch = useDispatch();
   const { user } = useContext(UserContext) || {};
   const normalizedCreateMode = String(createMode || "")
      .trim()
      .toLowerCase();
   const normalizedUserRole = String(
      user?.role ?? user?.Role ?? user?.userRole ?? "",
   )
      .trim()
      .toUpperCase();
   const normalizedUserRoles = Array.isArray(user?.roles)
      ? user.roles.map((entry) =>
           String(entry || "")
              .trim()
              .toUpperCase(),
        )
      : [];
   const isInstructorCreator =
      normalizedUserRole === "INSTRUCTOR" ||
      normalizedUserRoles.includes("INSTRUCTOR");
   const isSuperiorInstructorCreate = isInstructorSelfProfileMode(
      normalizedCreateMode,
   );
   const forcePurpleColor = isSuperiorInstructorCreate || isInstructorCreator;
   const canShowTicketFlags = showTicketFlags && !isSuperiorInstructorCreate;
   const canShowBlackoutAction =
      showBlackoutAction && !isSuperiorInstructorCreate;
   const canShowColorControl = showColorControl && !isSuperiorInstructorCreate;
   const canShowPersonalNote = showPersonalNote && !isSuperiorInstructorCreate;
   const rootRef = useRef(null);
   // ✅ ADD: autofocus pe inputul de telefon la deschiderea popup-ului
   const phoneInputRef = useRef(null);
   const didAutoFocusRef = useRef(false);

   const closingRef = useRef(false);

   const studentsAll = useSelector((s) => s.students?.list || []);
   const instructors = useSelector((s) => s.instructors?.list || []);
   const cars = useSelector((s) => s.cars?.list || []);
   const instructorsGroups = useSelector(
      (s) => s.instructorsGroups?.list || [],
   );
   const studentsAllList = Array.isArray(studentsAll)
      ? studentsAll
      : EMPTY_USERS;

   const [messages, setMessages] = useState([]);
   const pushPill = useCallback((text, type = "error") => {
      setMessages((prev) => [
         ...prev,
         { id: Date.now() + Math.random(), text, type },
      ]);
   }, []);
   const notifyCalendarPlus = useCallback((text, type = "info") => {
      const alertText = String(text || "").trim();
      if (!alertText) return;
      try {
         scheduleCalendarRefresh({
            source: "popup",
            type: "notify",
            alertType: type,
            alertText,
            forceReload: false,
         });
      } catch {}
   }, []);
   const popPill = useCallback(() => {
      setMessages((prev) => prev.slice(0, -1));
   }, []);

   const isInstructorSelectionLocked = !!lockInstructorSelection;
   const [view, setView] = useState("formSelect");

   const effectiveStartISO =
      (initialStartTime ?? (start ? new Date(start).toISOString() : null)) ||
      null;

   const initialInstructorIdNormalized = normalizeInstructorIdForCreate(
      initialInstructorId ?? instructorIdFromPayload ?? "",
   );
   const effectiveSector = normalizeCreateSector(
      sectorFromPayload ?? initialSector,
      requireInstructorSelection || deriveFromSelectedInstructor
         ? ""
         : "Botanica",
   );
   const effectiveGearbox = normalizeCreateGearbox(
      gearboxFromPayload ?? initialGearbox,
      requireInstructorSelection || deriveFromSelectedInstructor
         ? ""
         : "Manual",
   );

   const [sector, setSector] = useState(effectiveSector);
   const [gearbox, setGearbox] = useState(effectiveGearbox);
   const [studentId, setStudentId] = useState(
      initialStudentId != null ? String(initialStudentId) : "",
   );
   const [instructorId, setInstructorId] = useState(
      initialInstructorIdNormalized,
   );
   const [privateMessage, setPrivateMessage] = useState(
      typeof initialPrivateMessage === "string" ? initialPrivateMessage : "",
   );

   /* =========================
      ✅ ADD: Favorite / Important + Color (la creare)
   ========================= */
   const [isFavorite, setIsFavorite] = useState(initialIsFavorite === true);
   const [isImportant, setIsImportant] = useState(initialIsImportant === true);
   const [colorToken, setColorToken] = useState(() => {
      if (forcePurpleColor) return "--purple";
      return typeof initialColorToken === "string" && initialColorToken.trim()
         ? initialColorToken.trim()
         : "--black-t";
   });
   const selectableColorTokens = useMemo(() => {
      if (forcePurpleColor) return ["--purple"];
      const list = Array.isArray(availableColorTokens)
         ? availableColorTokens
         : [];
      const normalized = list
         .map((token) => String(token || "").trim())
         .filter(Boolean)
         .filter((token, index, arr) => arr.indexOf(token) === index);
      return normalized.length ? normalized : COLOR_TOKENS;
   }, [availableColorTokens, forcePurpleColor]);

   const [colorHoverText, setColorHoverText] = useState("");
   const colorHoverTimerRef = useRef(null);
   useEffect(() => {
      return () => {
         if (colorHoverTimerRef.current)
            clearTimeout(colorHoverTimerRef.current);
      };
   }, []);
   useEffect(() => {
      if (selectableColorTokens.includes(colorToken)) return;
      setColorToken(
         selectableColorTokens.includes("--black-t")
            ? "--black-t"
            : selectableColorTokens[0] || "--black-t",
      );
   }, [colorToken, selectableColorTokens]);
   useEffect(() => {
      if (!forcePurpleColor) return;
      if (colorToken === "--purple") return;
      setColorToken("--purple");
   }, [colorToken, forcePurpleColor]);
   const payloadColorToken = useMemo(() => {
      if (forcePurpleColor) return "--purple";
      if (!canShowColorControl) {
         return (
            selectableColorTokens.includes("--black-t")
               ? "--black-t"
               : selectableColorTokens[0] ||
                 (typeof initialColorToken === "string" &&
                    initialColorToken.trim()) ||
                 "--black-t"
         );
      }
      return colorToken || "--black-t";
   }, [
      canShowColorControl,
      colorToken,
      forcePurpleColor,
      initialColorToken,
      selectableColorTokens,
   ]);
   const payloadPrivateMessage = useMemo(() => {
      if (isSuperiorInstructorCreate || !canShowPersonalNote) return "";
      return privateMessage || "";
   }, [canShowPersonalNote, isSuperiorInstructorCreate, privateMessage]);
   useEffect(() => {
      if (!forcePurpleColor) return;
      if (isFavorite) return;
      setIsFavorite(true);
   }, [forcePurpleColor, isFavorite]);
   const payloadIsFavorite = useMemo(() => {
      if (forcePurpleColor) return true;
      return canShowTicketFlags ? !!isFavorite : false;
   }, [canShowTicketFlags, forcePurpleColor, isFavorite]);
   const payloadIsImportant = useMemo(
      () => (canShowTicketFlags ? !!isImportant : false),
      [canShowTicketFlags, isImportant],
   );

   const [newFullName, setNewFullName] = useState("");
   const [phoneFull, setPhoneFull] = useState("");
   const deferredPhoneFull = useDeferredValue(phoneFull);
   const [highlightedStudentId, setHighlightedStudentId] = useState(null);
   const [continuing, setContinuing] = useState(false);
   const allowPhoneAutoFocus = useMemo(
      () => shouldAutoFocusCreatePhoneInput(),
      [],
   );
   useEffect(() => {
      // focus doar la deschiderea popup-ului (prima randare) și doar când e view-ul corect
      if (!allowPhoneAutoFocus) return;
      if (didAutoFocusRef.current) return;
      if (view !== "formSelect") return;
      if (continuing) return;

      didAutoFocusRef.current = true;

      let raf1 = 0;
      let raf2 = 0;

      raf1 = requestAnimationFrame(() => {
         raf2 = requestAnimationFrame(() => {
            const el = phoneInputRef.current;
            if (!el) return;

            try {
               el.focus({ preventScroll: true });
            } catch {
               el.focus();
            }

            // opțional: cursor la final
            try {
               const len = (el.value || "").length;
               el.setSelectionRange(len, len);
            } catch {}
         });
      });

      return () => {
         cancelAnimationFrame(raf1);
         cancelAnimationFrame(raf2);
      };
   }, [allowPhoneAutoFocus, view, continuing]);

   const [selectedDate] = useState(() => {
      const d1 = initialDate ? safeLocalDateFromYMD(initialDate) : null;
      if (d1) return d1;

      if (effectiveStartISO) return new Date(effectiveStartISO);
      if (start) return new Date(start);
      return null;
   });

   const [selectedTime] = useState(() => {
      const hhmmFromProps = (initialTime || "").trim();
      const hhmm =
         hhmmFromProps ||
         (effectiveStartISO
            ? hhmmInTZ(new Date(effectiveStartISO), MOLDOVA_TZ)
            : "");

      if (!hhmm) return null;
      const match = oreDisponibile.find((o) => o.oraStart === hhmm);
      return match || { eticheta: hhmm, oraStart: hhmm };
   });

   const [qInstructor, setQInstructor] = useState("");

   const [hasBlackout, setHasBlackout] = useState(initialSlotIsBlocked === true);
   const [blackoutId, setBlackoutId] = useState(null);
   const [blackoutType, setBlackoutType] = useState(
      initialSlotIsBlocked === true ? "SINGLE" : null,
   );
   const [checkingBlackout, setCheckingBlackout] = useState(false);
   const [blocking, setBlocking] = useState(false);

   useEffect(() => {
      if (!studentsAllList.length) dispatch(fetchStudents());
      if (
         (view === "searchInstructor" || view === "formSelect") &&
         !instructors?.length
      ) {
         dispatch(fetchInstructors());
      }
      if (
         (view === "searchInstructor" || view === "formSelect") &&
         !cars?.length
      ) {
         dispatch(fetchCars());
      }
      if (
         (view === "searchInstructor" || view === "formSelect") &&
         !instructorsGroups?.length
      ) {
         dispatch(fetchInstructorsGroups());
      }
   }, [
      dispatch,
      studentsAllList.length,
      instructors?.length,
      cars?.length,
      instructorsGroups?.length,
      view,
   ]);

   useEffect(() => {
      if (!isInstructorSelectionLocked) return;
      if (view !== "searchInstructor") return;
      setView("formSelect");
   }, [isInstructorSelectionLocked, view]);

   const {
      searchIndex: studentsSearchIndex,
      exactPhoneDigits: studentsByExactPhoneDigits,
   } = useMemo(
      () => getCachedStudentLookup(studentsAllList),
      [studentsAllList],
   );
   const studentsByExactPhoneDigitsRef = useRef(studentsByExactPhoneDigits);
   useEffect(() => {
      studentsByExactPhoneDigitsRef.current = studentsByExactPhoneDigits;
   }, [studentsByExactPhoneDigits]);

   const selectedStudent = useMemo(() => {
      if (!studentId) return null;
      const sid = String(studentId);
      for (const user of studentsAllList) {
         if (!hasUserRole(user)) continue;
         if (String(user?.id ?? "") === sid) return user;
      }
      return null;
   }, [studentsAllList, studentId]);
   const normalizedInstructorId = useMemo(
      () => normalizeInstructorIdForCreate(instructorId),
      [instructorId],
   );
   const resolvedInstructorId =
      normalizedInstructorId || initialInstructorIdNormalized;
   const selectedInstructor = useMemo(
      () =>
         resolvedInstructorId
            ? instructors.find(
                 (i) => String(i.id) === String(resolvedInstructorId),
              )
            : null,
      [instructors, resolvedInstructorId],
   );
   const selectedCar = useMemo(
      () =>
         resolvedInstructorId
            ? (cars || []).find(
                 (car) =>
                    String(car?.instructorId ?? car?.instructor_id ?? "") ===
                    String(resolvedInstructorId),
              ) || null
            : null,
      [cars, resolvedInstructorId],
   );
   const selectedInstructorSector = useMemo(
      () =>
         resolvedInstructorId
            ? resolveSectorFromInstructorForCreate({
                 instructorId: resolvedInstructorId,
                 instructor: selectedInstructor,
                 instructorsGroups,
                 fallback: "",
              })
            : "",
      [resolvedInstructorId, selectedInstructor, instructorsGroups],
   );
   const selectedInstructorGearbox = useMemo(
      () =>
         normalizeCreateGearbox(
            selectedCar?.gearbox ??
               selectedCar?.transmission ??
               selectedCar?.gearboxType ??
               selectedInstructor?.gearbox ??
               selectedInstructor?.transmission ??
               selectedInstructor?.car?.gearbox ??
               selectedInstructor?.car?.transmission,
            "",
         ),
      [selectedCar, selectedInstructor],
   );

   useEffect(() => {
      if (normalizedInstructorId) return;
      if (!initialInstructorIdNormalized) return;
      setInstructorId((prev) => {
         if (normalizeInstructorIdForCreate(prev)) return prev;
         return initialInstructorIdNormalized;
      });
   }, [normalizedInstructorId, initialInstructorIdNormalized]);

   useEffect(() => {
      if (!resolvedInstructorId) {
         if (requireInstructorSelection || deriveFromSelectedInstructor) {
            setSector((prev) => (prev ? "" : prev));
            setGearbox((prev) => (prev ? "" : prev));
         }
         return;
      }

      const isInitialInstructorSelected =
         initialInstructorIdNormalized &&
         String(initialInstructorIdNormalized) === String(resolvedInstructorId);
      const nextSector =
         selectedInstructorSector ||
         (isInitialInstructorSelected
            ? normalizeCreateSector(effectiveSector, "")
            : "");
      const nextGearbox =
         selectedInstructorGearbox ||
         (isInitialInstructorSelected
            ? normalizeCreateGearbox(effectiveGearbox, "")
            : "");

      setSector((prev) => (prev === nextSector ? prev : nextSector));
      setGearbox((prev) => (prev === nextGearbox ? prev : nextGearbox));
   }, [
      resolvedInstructorId,
      initialInstructorIdNormalized,
      selectedInstructorSector,
      selectedInstructorGearbox,
      requireInstructorSelection,
      deriveFromSelectedInstructor,
      effectiveSector,
      effectiveGearbox,
   ]);

   const filteredStudents = useMemo(() => {
      return findStudentsForSearch(
         studentsSearchIndex,
         deferredPhoneFull,
         SEARCH_RESULTS_LIMIT * 2,
      );
   }, [studentsSearchIndex, deferredPhoneFull]);

   const assignableInstructors = useMemo(
      () => (instructors || []).filter(canOwnCalendarReservations),
      [instructors],
   );

   const filteredInstructors = useMemo(() => {
      const q = (qInstructor || "").trim().toLowerCase();
      if (!q) return assignableInstructors.slice(0, SEARCH_RESULTS_LIMIT);
      return assignableInstructors
         .filter((i) => {
            const full =
               `${i.firstName || ""} ${i.lastName || ""}`.toLowerCase();
            const phone = (i.phone || "").toLowerCase();
            return full.includes(q) || phone.includes(q);
         })
         .slice(0, SEARCH_RESULTS_LIMIT);
   }, [assignableInstructors, qInstructor]);

   const studentsForSearchList = useMemo(() => {
      const base = Array.isArray(filteredStudents) ? filteredStudents : [];
      if (!highlightedStudentId) return base.slice(0, SEARCH_RESULTS_LIMIT);
      const idStr = String(highlightedStudentId);
      const idx = base.findIndex((s) => String(s.id) === idStr);
      if (idx === -1) return base.slice(0, SEARCH_RESULTS_LIMIT);
      const arr = [...base];
      const [match] = arr.splice(idx, 1);
      return [match, ...arr].slice(0, SEARCH_RESULTS_LIMIT);
   }, [filteredStudents, highlightedStudentId]);

   const studentDisplay = selectedStudent
      ? `${selectedStudent.firstName || ""} ${
           selectedStudent.lastName || ""
        }`.trim()
      : "ne ales";

   const instructorDisplay = selectedInstructor
      ? `${selectedInstructor.firstName || ""} ${
           selectedInstructor.lastName || ""
        }`.trim()
      : "ne ales";

   const studentPhone =
      selectedStudent?.phone ||
      selectedStudent?.phoneNumber ||
      selectedStudent?.mobile ||
      selectedStudent?.telefon ||
      "";
   const instructorPhone = selectedInstructor?.phone || "";

   /* =========================
      join/leave create-draft
   ========================= */
   const wsRef = useRef(null);
   useEffect(() => {
      wsRef.current =
         typeof window !== "undefined" ? window.__reservationWS : null;
   }, []);

   const draftSuffix = useMemo(() => {
      const raw = String(draftSlotKey || "").trim();
      if (raw.includes("|")) {
         return raw.split("|").slice(1).join("|").trim();
      }

      if (effectiveStartISO) {
         const d = new Date(effectiveStartISO);
         return Number.isFinite(d.getTime()) ? d.toISOString() : "";
      }
      if (selectedDate && selectedTime?.oraStart) {
         return toUtcIsoFromLocal(selectedDate, selectedTime.oraStart);
      }
      return "";
   }, [draftSlotKey, effectiveStartISO, selectedDate, selectedTime]);

   const draftRoomKey = useMemo(() => {
      const inst = String(resolvedInstructorId || "").trim();
      const suf = String(draftSuffix || "").trim();
      if (!inst || !suf) return "";
      return `${inst}|${suf}`;
   }, [resolvedInstructorId, draftSuffix]);

   const activeKeyRef = useRef("");
   useEffect(() => {
      activeKeyRef.current = draftRoomKey;
   }, [draftRoomKey]);

   const leftRef = useRef(new Set());
   const skipFirstDraftJoinRef = useRef(!!draftPresenceAlreadyStarted);

   const getWS = () =>
      (typeof window !== "undefined" ? window.__reservationWS : null) ||
      wsRef.current;

   const joinDraft = useCallback(
      (key) => {
         if (!key) return;
         leftRef.current.delete(key);

         const ws = getWS();
         ws?.joinCreateDraft?.(key, { sector, gearbox });

         if (typeof window !== "undefined" && window.__WS_DEBUG) {
         }
      },
      [sector, gearbox],
   );

   const leaveDraft = useCallback(
      (key) => {
         if (!key) return;
         if (leftRef.current.has(key)) return;
         leftRef.current.add(key);

         const ws = getWS();
         ws?.leaveCreateDraft?.(key, { sector, gearbox });

         if (typeof window !== "undefined" && window.__WS_DEBUG) {
         }
      },
      [sector, gearbox],
   );

   useEffect(() => {
      if (!draftRoomKey) return;

      if (skipFirstDraftJoinRef.current) {
         // Calendarul a pornit deja draft-ul înainte de openPopup.
         // Evităm emit-ul duplicat "reservation:create:started".
         skipFirstDraftJoinRef.current = false;
         return () => {
            leaveDraft(draftRoomKey);
         };
      }

      joinDraft(draftRoomKey);

      return () => {
         leaveDraft(draftRoomKey);
      };
   }, [draftRoomKey, joinDraft, leaveDraft]);

   useEffect(() => {
      const forceLeave = () => {
         const key = activeKeyRef.current;
         if (key) leaveDraft(key);
      };

      const onPageHide = () => forceLeave();
      const onBeforeUnload = () => forceLeave();
      const onVis = () => {
         if (document.hidden) forceLeave();
      };

      window.addEventListener("pagehide", onPageHide);
      window.addEventListener("beforeunload", onBeforeUnload);
      document.addEventListener("visibilitychange", onVis);

      return () => {
         window.removeEventListener("pagehide", onPageHide);
         window.removeEventListener("beforeunload", onBeforeUnload);
         document.removeEventListener("visibilitychange", onVis);
      };
   }, [leaveDraft]);

   const closeSelf = useCallback(() => {
      if (closingRef.current) return;
      closingRef.current = true;

      const key = activeKeyRef.current || draftRoomKey;
      try {
         leaveDraft(key);
      } catch {}

      if (typeof onClose === "function") {
         onClose();
         return;
      }

      try {
         closeSubPopupStore();
      } catch {}
      try {
         closePopupStore();
      } catch {}
   }, [onClose, leaveDraft, draftRoomKey]);

   useEffect(() => {
      closingRef.current = false;

      const onKeyDown = (e) => {
         if (e.key === "Escape") closeSelf();
      };

      document.addEventListener("keydown", onKeyDown, true);

      return () => {
         document.removeEventListener("keydown", onKeyDown, true);
      };
   }, [closeSelf]);

   const sleep = useCallback((ms) => new Promise((r) => setTimeout(r, ms)), []);
   const waitUntilStudentInListByPhone = useCallback(
      async (phoneDigits, tries = 12, delayMs = 250) => {
         const digits = (phoneDigits || "").replace(/\D/g, "");
         for (let i = 0; i < tries; i++) {
            try {
               await dispatch(fetchStudents());
            } catch {}
            const found = findStudentByPhoneDigits(
               studentsByExactPhoneDigitsRef.current,
               digits,
            );
            if (found?.id) return String(found.id);
            await sleep(delayMs);
         }
         return "";
      },
      [dispatch, sleep],
   );

   const ensureStudentCreated = useCallback(async () => {
      const { firstName, lastName } = splitFullName(newFullName);
      if (!firstName || !lastName) {
         pushPill("Scrie numele complet (ex.: Ion Popescu).");
         return "";
      }

      const rawDigits = onlyDigits(phoneFull);
      if (!rawDigits) {
         pushPill("Introdu un număr de telefon (doar cifre).");
         return "";
      }
      const digits = normalizePhoneForStudentCreate(rawDigits);

      setHighlightedStudentId(null);

      const existingSamePhone =
         findStudentByPhoneDigits(
            studentsByExactPhoneDigitsRef.current,
            rawDigits,
         ) ||
         findStudentByPhoneDigits(
            studentsByExactPhoneDigitsRef.current,
            digits,
         );
      if (existingSamePhone) {
         const idStr = String(existingSamePhone.id);
         setStudentId(idStr);
         setNewFullName("");
         setPhoneFull("");
         setHighlightedStudentId(idStr);
         setView("formSelect");
         pushPill(
            "Există deja un elev cu acest număr. A fost selectat automat.",
            "info",
         );
         return idStr;
      }

      // cont de oaspete (doar nume + telefon): fără grupă, nu intră în
      // platformă și nu primește notificări, emailuri sau mementouri
      try {
         const res = await apiClientService.post("/users/guests", {
            firstName,
            lastName,
            phone: digits,
         });

         if (res.ok) {
            let created = {};
            try {
               created = await res.json();
            } catch {}

            let newId = String(created?.id ?? "") || "";
            if (!newId) newId = await waitUntilStudentInListByPhone(digits);

            if (!newId) {
               pushPill(
                  "Oaspete creat, dar nu am putut identifica ID-ul.",
                  "warning",
               );
               return "";
            }

            try {
               await dispatch(fetchStudents());
            } catch {}

            setStudentId(newId);
            setNewFullName("");
            setPhoneFull("");
            setHighlightedStudentId(newId);
            setView("formSelect");
            pushPill("Oaspete nou adăugat (fără acces în platformă).", "success");
            return newId;
         }

         let errJson = null;
         try {
            errJson = await res.json();
         } catch {}
         const message = Array.isArray(errJson?.message)
            ? errJson.message.join(" ")
            : errJson?.message;
         pushPill(message || "Nu am putut crea oaspetele.", "error");
      } catch (e) {
         pushPill(e?.message || "Eroare la crearea oaspetelui.", "error");
      }

      const fallbackId = await waitUntilStudentInListByPhone(digits);
      if (fallbackId) {
         try {
            await dispatch(fetchStudents());
         } catch {}

         setStudentId(fallbackId);
         setNewFullName("");
         setPhoneFull("");
         setHighlightedStudentId(fallbackId);
         setView("formSelect");
         pushPill("Elev existent selectat automat după telefon.", "success");
         return fallbackId;
      }

      pushPill(
         "Nu am putut crea elevul și nu l-am găsit după telefon.",
         "error",
      );
      return "";
   }, [
      dispatch,
      newFullName,
      phoneFull,
      pushPill,
      waitUntilStudentInListByPhone,
   ]);

   const [saving, setSaving] = useState(false);
   const saveInFlightRef = useRef(false);

   const notifyBlackoutsChanged = useCallback((iid) => {
      const id = String(iid ?? "").trim();
      if (!id) return;

      try {
         scheduleCalendarRefresh({
            source: "popup",
            type: "blackouts-changed",
            instructorId: id,
            forceReload: false,
         });
      } catch {}
   }, []);

   const runPostCreateSync = useCallback(
      ({
         createResult,
         startTimeToSend,
         studentIdNum,
         instructorIdNum,
         optimisticId,
         skipHeavySync = false,
      }) => {
         if (skipHeavySync) return;
         // rulează după close, ca UI-ul să rămână instant
         setTimeout(() => {
            const sync = async () => {
               try {
                  const targetLocalKey =
                     busyLocalKeyFromStored(startTimeToSend);
                  let createdCandidates = extractCreatedReservations(
                     createResult,
                  ).filter(
                     (row) =>
                        Number.isFinite(row?.id) &&
                        Number.isFinite(row?.userId) &&
                        Number(row.userId) === studentIdNum &&
                        (!targetLocalKey ||
                           (row?.startRaw &&
                              busyLocalKeyFromStored(row.startRaw) ===
                                 targetLocalKey)),
                  );

                  if (!createdCandidates.length) {
                     try {
                        const fromUserRaw =
                           await getUserReservations(studentIdNum);
                        const fromUser = Array.isArray(fromUserRaw)
                           ? fromUserRaw
                           : Array.isArray(fromUserRaw?.items)
                             ? fromUserRaw.items
                             : [];

                        createdCandidates = fromUser
                           .map((row) => ({
                              id: Number(reservationIdFromAny(row)),
                              instructorId: Number(
                                 reservationInstructorIdFromAny(row),
                              ),
                              userId: Number(reservationUserIdFromAny(row)),
                              startRaw: String(
                                 reservationStartFromAny(row) || "",
                              ),
                           }))
                           .filter(
                              (row) =>
                                 Number.isFinite(row.id) &&
                                 (!targetLocalKey ||
                                    (row.startRaw &&
                                       busyLocalKeyFromStored(row.startRaw) ===
                                          targetLocalKey)),
                           );
                     } catch {}
                  }

                  const hasKnownCreatedId = createdCandidates.some((row) =>
                     Number.isFinite(row?.id),
                  );
                  let shouldFetchDelta = !hasKnownCreatedId;
                  const hasIncompleteInstructor = createdCandidates.some(
                     (row) => !Number.isFinite(row?.instructorId),
                  );
                  if (hasIncompleteInstructor) shouldFetchDelta = true;

                  const hasUnexpectedInstructor = createdCandidates.some(
                     (row) =>
                        Number.isFinite(row.id) &&
                        Number.isFinite(row.instructorId) &&
                        row.instructorId !== instructorIdNum,
                  );
                  if (hasUnexpectedInstructor) shouldFetchDelta = true;

                  notifyBlackoutsChanged(instructorIdNum);

                  if (shouldFetchDelta) {
                     try {
                        await (dispatch(fetchReservationsDelta()).unwrap?.() ??
                           dispatch(fetchReservationsDelta()));
                     } catch {}
                  }

                  try {
                     triggerCalendarRefresh({
                        source: "popup",
                        type: "create-postsync",
                        forceReload: false,
                     });
                  } catch {}
               } catch (err) {
                  notifyCalendarPlus(
                     err?.message ||
                        "Nu am putut verifica automat programarea noua dupa creare.",
                     "warning",
                  );
               } finally {
                  if (optimisticId) {
                     dispatch(removeReservationLocal(optimisticId));
                  }
               }
            };

            sync();
         }, 0);
      },
      [dispatch, notifyBlackoutsChanged, notifyCalendarPlus],
   );

   const onSave = useCallback(async () => {
      if (saving || continuing || saveInFlightRef.current) return;
      if (!resolvedInstructorId) return pushPill("Selectează instructorul.");
      if (!selectedDate || !selectedTime) {
         return pushPill("Lipsește ziua sau ora programării (din props).");
      }
      if (
         isInstructorSelectionLocked &&
         hasCalendarSlotInstructorMismatch(
            calendarSlotKeyFromPayload,
            initialInstructorIdNormalized,
         )
      ) {
         return pushPill(
            "Coloana instructorului s-a schimbat între timp. Închide fereastra și selectează din nou ora.",
         );
      }

      let effectiveStudentId = String(studentId || "").trim();
      if (!effectiveStudentId) {
         const hasInlineStudentDraft =
            newFullName.trim().split(/\s+/).length >= 2 &&
            onlyDigits(phoneFull).length > 0;
         if (!hasInlineStudentDraft) {
            return pushPill(
               "Selectează elevul sau completează nume + telefon pentru elev nou.",
            );
         }

         setContinuing(true);
         try {
            effectiveStudentId = String(
               (await ensureStudentCreated()) || "",
            ).trim();
         } finally {
            setContinuing(false);
         }
         if (!effectiveStudentId) return;
      }

      const iso = toUtcIsoFromLocal(selectedDate, selectedTime.oraStart);

      const instructorIdNum = isInstructorSelectionLocked
         ? Number(initialInstructorIdNormalized)
         : Number(resolvedInstructorId);
      const studentIdNum = Number(effectiveStudentId);

      if (!Number.isFinite(instructorIdNum) || instructorIdNum <= 0) {
         return pushPill(
            "Instructor invalid (ID). Încearcă să-l selectezi din nou.",
         );
      }
      if (!Number.isFinite(studentIdNum) || studentIdNum <= 0) {
         return pushPill("Elev invalid (ID). Încearcă să-l selectezi din nou.");
      }

      if (saveInFlightRef.current) return;
      saveInFlightRef.current = true;
      setSaving(true);

      const startTimeToSend =
         BUSY_KEYS_MODE === "local-match" ? isoForDbMatchLocalHour(iso) : iso;
      const isInitialInstructorSelected =
         initialInstructorIdNormalized &&
         String(initialInstructorIdNormalized) === String(instructorIdNum);
      const sectorToSend = resolveSectorFromInstructorForCreate({
         instructorId: instructorIdNum,
         instructor: selectedInstructor,
         instructorsGroups,
         fallback: isInitialInstructorSelected
            ? normalizeCreateSector(effectiveSector, "")
            : "",
      });
      const gearboxToSend = normalizeCreateGearbox(
         selectedInstructorGearbox ||
            (isInitialInstructorSelected
               ? normalizeCreateGearbox(effectiveGearbox, "")
               : ""),
         "Manual",
      );
      const payload = {
         userId: studentIdNum,
         instructorId: instructorIdNum,
         reservations: [
            {
               startTime: startTimeToSend,
               ...(sectorToSend ? { sector: sectorToSend } : {}),
               gearbox:
                  gearboxToSend.toLowerCase() === "automat"
                     ? "Automat"
                     : "Manual",
               privateMessage: payloadPrivateMessage,
               // ✅ CHANGE: culoare + tichete
               color: payloadColorToken,
               isFavorite: payloadIsFavorite,
               isImportant: payloadIsImportant,
               instructorId: instructorIdNum,
            },
         ],
      };
      const targetLocalKey = busyLocalKeyFromStored(startTimeToSend);

      debugCreateReservation("submit-payload", {
         payload,
         uiState: {
            studentId: effectiveStudentId,
            resolvedInstructorId,
            selectedInstructorId: selectedInstructor?.id ?? null,
            selectedInstructorSector,
            selectedInstructorGearbox,
            sectorState: sector,
            gearboxState: gearbox,
            effectiveSector,
            effectiveGearbox,
            selectedDate:
               selectedDate instanceof Date &&
               Number.isFinite(selectedDate.getTime())
                  ? selectedDate.toISOString()
                  : null,
            selectedTime: selectedTime?.oraStart ?? null,
         },
      });
      debugCreateReservation("precheck-local-disabled", {
         reason:
            "Conflictul este validat strict la submit pe server pentru a evita false-positive din cache local stale.",
         payload,
      });

      const optimisticId = `tmp-create-popup-${Date.now()}-${Math.random()
         .toString(36)
         .slice(2, 8)}`;
      const optimisticStartLocal = `${selectedDate.getFullYear()}-${String(
         selectedDate.getMonth() + 1,
      ).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(
         2,
         "0",
      )}T${selectedTime.oraStart}:00`;
      const optimisticUser = selectedStudent
         ? {
              id: selectedStudent.id ?? studentIdNum,
              firstName: selectedStudent.firstName ?? "",
              lastName: selectedStudent.lastName ?? "",
              phone:
                 selectedStudent.phone ??
                 selectedStudent.phoneNumber ??
                 selectedStudent.mobile ??
                 "",
              privateMessage: selectedStudent.privateMessage ?? "",
           }
         : null;

      const optimisticEvent = {
         id: optimisticId,
         userId: studentIdNum,
         instructorId: instructorIdNum,
         startTime: optimisticStartLocal,
         sector: sectorToSend || null,
         gearbox:
            gearboxToSend.toLowerCase() === "automat" ? "Automat" : "Manual",
         privateMessage: payloadPrivateMessage,
         color: payloadColorToken,
         isFavorite: payloadIsFavorite,
         isImportant: payloadIsImportant,
         _optimistic: true,
         _optimisticPending: true,
         ...(optimisticUser ? { user: optimisticUser } : {}),
      };

      dispatch(addReservationLocal(optimisticEvent));

      const calendarStore = getCalendarStore();
      const createSlotKey = resolveCalendarSlotKeyForCreate({
         calendarSlotKey: calendarSlotKeyFromPayload,
         calendarDayIndex: calendarDayIndexFromPayload,
         instructorId: instructorIdNum,
         slotLabel: selectedTime?.oraStart || initialTime || "",
      });
      const optimisticTitle = optimisticUser
         ? `${optimisticUser.firstName || ""} ${optimisticUser.lastName || ""}`.trim()
         : "Rezervare";
      const createVisualItem = buildOptimisticSlotVisualItem({
         id: optimisticId,
         userId: studentIdNum,
         instructorId: instructorIdNum,
         title: optimisticTitle || "Rezervare",
         phone: optimisticUser?.phone || "",
         privateMessage: payloadPrivateMessage,
         note: payloadPrivateMessage,
         color: payloadColorToken,
         isFavorite: payloadIsFavorite,
         isImportant: payloadIsImportant,
         sector: sectorToSend || "",
         gearbox:
            gearboxToSend.toLowerCase() === "automat" ? "Automat" : "Manual",
         timeText: selectedTime?.oraStart || "",
      });
      if (calendarStore && createSlotKey) {
         applyOptimisticUpsert(calendarStore, {
            event: optimisticEvent,
            slotKey: createSlotKey,
            commandId: `create-${optimisticId}`,
            patchBus: calendarStore.patchBus,
            visualItem: createVisualItem,
         });
      } else if (!createSlotKey) {
         debugCreateReservation("create-missing-slot-key", {
            calendarSlotKeyFromPayload,
            calendarDayIndexFromPayload,
            instructorIdNum,
            slotLabel: selectedTime?.oraStart || initialTime || "",
         });
      }

      void enqueueCalendarMutation(
         async () => {
         try {
            const createResult = await createReservationsForUser(payload);
            debugCreateReservation("create-success", {
               payload,
               createResult,
            });

            // Refresh blackout UI if create succeeded on a previously blocked slot.
            if (hasBlackout) {
               try {
                  const slotKeyForPatch =
                     selectedSlotLocalKey(selectedDate, selectedTime) ||
                     targetLocalKey ||
                     busyLocalKeyFromStored(startTimeToSend) ||
                     "";
                  if (slotKeyForPatch) {
                     triggerCalendarRefresh({
                        source: "popup",
                        type: "blackout-slot-patch",
                        instructorId: String(instructorIdNum),
                        op: "remove",
                        slotKey: slotKeyForPatch,
                        forceReload: false,
                     });
                  }
               } catch {}
               setHasBlackout(false);
               setBlackoutId(null);
               setBlackoutType(null);
               notifyBlackoutsChanged(instructorIdNum);
            }

            const { createdId, confirmedEvent, needsPostSync } =
               await resolvePopupCreateConfirmation({
                  createResult,
                  studentIdNum,
                  instructorIdNum,
                  targetLocalKey,
                  optimisticStartLocal,
                  optimisticUser,
                  sectorToSend,
                  gearboxToSend,
                  payloadPrivateMessage,
                  payloadColorToken,
                  payloadIsFavorite,
                  payloadIsImportant,
               });

            if (!createdId) {
               notifyCalendarPlus(
                  "Programarea a fost trimisa, dar serverul nu a confirmat-o imediat. Verific automat calendarul.",
                  "warning",
               );
            } else if (confirmedEvent) {
               const deletedWhileCreating =
                  calendarStore?.isTombstoned?.(createdId) === true ||
                  (calendarStore?.isTombstoned?.(optimisticId) === true &&
                     !calendarStore?.getEventById?.(optimisticId) &&
                     !calendarStore?.getEventById?.(createdId));

               if (deletedWhileCreating) {
                  dispatch(removeReservationLocal(optimisticId));
                  dispatch(removeReservationLocal(createdId));
                  if (calendarStore) {
                     applyOptimisticRemove(calendarStore, {
                        eventId: optimisticId,
                        patchBus: calendarStore.patchBus,
                        tombstone: true,
                     });
                     applyOptimisticRemove(calendarStore, {
                        eventId: createdId,
                        patchBus: calendarStore.patchBus,
                        tombstone: true,
                     });
                  }
                  void deleteReservation(createdId).catch(() => {});
                  closeSelf();
                  return {
                     ok: true,
                     createdId: "",
                     cancelledByDelete: true,
                     eventSnapshot: null,
                     slotKey: "",
                  };
               }

               dispatch(removeReservationLocal(createdId));
               dispatch(removeReservationLocal(optimisticId));
               dispatch(addReservationLocal(confirmedEvent));

               if (calendarStore) {
                  const resolvedSlotKey =
                     createSlotKey ||
                     calendarStore.getEventSlotKey(optimisticId) ||
                     "";
                  const replaced = applyOptimisticReplaceId(calendarStore, {
                     fromId: optimisticId,
                     toId: createdId,
                     nextEvent: confirmedEvent,
                     slotKey: resolvedSlotKey,
                     patchBus: calendarStore.patchBus,
                  });
                  if (!replaced) {
                     dispatch(removeReservationLocal(createdId));
                     dispatch(removeReservationLocal(optimisticId));
                     void deleteReservation(createdId).catch(() => {});
                     closeSelf();
                     return {
                        ok: true,
                        createdId: "",
                        cancelledByDelete: true,
                        eventSnapshot: null,
                        slotKey: "",
                     };
                  }
                  compactPatchBusAfterIdPromotion(calendarStore.patchBus, {
                     fromId: optimisticId,
                     toId: createdId,
                     slotKey: resolvedSlotKey,
                  });
                  completePendingCommand(calendarStore, createdId);
               }
            }

            runPostCreateSync({
               createResult,
               startTimeToSend,
               studentIdNum,
               instructorIdNum,
               optimisticId,
               skipHeavySync: !needsPostSync,
            });
            closeSelf();
            return {
               ok: true,
               createdId,
               eventSnapshot: confirmedEvent,
               slotKey:
                  createSlotKey ||
                  calendarStore?.getEventSlotKey?.(createdId) ||
                  "",
            };
         } catch (e) {
            debugCreateReservation("create-failed", {
               payload,
               error: e?.message || String(e || ""),
               rawError: e,
            });
            dispatch(removeReservationLocal(optimisticId));
            if (calendarStore) {
               applyOptimisticRemove(calendarStore, {
                  eventId: optimisticId,
                  patchBus: calendarStore.patchBus,
                  tombstone: false,
               });
            }
            const formattedError = formatCalendarCreateError(e, {
               fallback:
                  "Programarea noua nu a putut fi creata. Verifica si incearca din nou.",
            });
            notifyCalendarPlus(
               formattedError.text,
               formattedError.isConflict ? "warning" : "error",
            );
            try {
               scheduleCalendarRefresh({
                  source: "popup",
                  type: "create-failed",
                  instructorId: String(instructorIdNum),
                  forceReload: true,
               });
            } catch {}
            return {
               ok: false,
               error: formattedError.text,
            };
         } finally {
            setSaving(false);
            saveInFlightRef.current = false;
         }
      },
      {
         buildUndoEntry: (result) => {
            const createdId = String(result?.createdId || "").trim();
            const eventSnapshot = result?.eventSnapshot || null;
            if (!createdId || !eventSnapshot) return null;
            const store = getCalendarStore();
            if (!store) return null;
            return buildCreateUndoEntry({
               store,
               dispatch,
               reservationId: createdId,
               eventSnapshot,
               slotKey: String(result?.slotKey || "").trim(),
               onAlert: (type, text) => {
                  notifyCalendarPlus(
                     text,
                     type === "error"
                        ? "error"
                        : type === "warning"
                          ? "warning"
                          : "success",
                  );
               },
            });
         },
      },
      ).then((result) => {
         if (result?.ok === false) {
            const message = String(
               result?.error ||
                  "Operația calendar nu a putut fi finalizată.",
            );
            notifyCalendarPlus(message, "error");
         }
      });
   }, [
      saving,
      continuing,
      studentId,
      resolvedInstructorId,
      selectedDate,
      selectedTime,
      newFullName,
      phoneFull,
      sector,
      gearbox,
      effectiveSector,
      effectiveGearbox,
      initialInstructorIdNormalized,
      isInstructorSelectionLocked,
      calendarSlotKeyFromPayload,
      payloadColorToken,
      payloadPrivateMessage,
      payloadIsFavorite,
      payloadIsImportant,
      pushPill,
      closeSelf,
      dispatch,
      selectedStudent,
      selectedInstructor,
      selectedInstructorSector,
      selectedInstructorGearbox,
      instructorsGroups,
      runPostCreateSync,
      notifyCalendarPlus,
      notifyBlackoutsChanged,
      ensureStudentCreated,
      hasBlackout,
   ]);

   const validNewName = newFullName.trim().split(/\s+/).length >= 2;
   const validNewPhone = onlyDigits(phoneFull).length > 0;
   const canSubmitWithStudentData =
      Boolean(studentId) || (validNewName && validNewPhone);

   useEffect(() => {
      // La potrivire exactă după telefon, selectăm automat elevul.
      if (view !== "formSelect") return;

      const digits = onlyDigits(phoneFull);
      if (!digits) {
         setHighlightedStudentId(null);
         return;
      }

      const exactByPhone = digits
         ? findStudentByPhoneDigits(studentsByExactPhoneDigits, digits)
         : null;
      const exact = exactByPhone;

      if (exact) {
         const exactId = String(exact.id);
         setHighlightedStudentId(exactId);
         const canAutoSelect = digits.length > 5;
         if (!canAutoSelect) return;
         setStudentId((prev) =>
            String(prev ?? "") === exactId ? prev : exactId,
         );
      } else {
         setHighlightedStudentId(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [phoneFull, studentsByExactPhoneDigits, view]);

   const handlePhoneKeyDown = (e) => {
      if (e.key === "Enter") {
         e.preventDefault();
         if (continuing) return;

         const digits = onlyDigits(phoneFull);
         if (!digits) return;

         const exact = findStudentByPhoneDigits(
            studentsByExactPhoneDigitsRef.current,
            digits,
         );

         if (exact) {
            const idStr = String(exact.id);
            setHighlightedStudentId(idStr);
            setStudentId((prev) =>
               String(prev ?? "") === idStr ? prev : idStr,
            );
            pushPill("Elev selectat automat după telefon.", "success");
         } else {
            pushPill(
               "Nu exista potrivire exacta dupa telefon. Verifica numarul sau alege elevul din lista.",
               "warning",
            );
         }
      }
   };

   const studentLabel =
      studentDisplay !== "ne ales" ? studentDisplay : "Alege elev";
   const instructorLabel =
      instructorDisplay !== "ne ales" ? instructorDisplay : "Alege instructor";

   const dateLabel = selectedDate
      ? formatDateRO(selectedDate.toISOString())
      : "Nu este setată";
   const timeLabel = selectedTime?.eticheta || "Nu este setată";

   /* ====== Blackout detect ====== */
   useEffect(() => {
      if (!canShowBlackoutAction || view !== "formSelect") {
         setCheckingBlackout(false);
         if (view !== "formSelect") {
            setHasBlackout(false);
            setBlackoutId(null);
            setBlackoutType(null);
         }
         return;
      }

      if (!resolvedInstructorId || !selectedDate || !selectedTime) {
         setHasBlackout(false);
         setBlackoutId(null);
         setBlackoutType(null);
         return;
      }

      let cancelled = false;

      const run = async () => {
         setCheckingBlackout(true);
         // Optimistic label while fetching — then API is source of truth.
         if (initialSlotIsBlocked === true) {
            setHasBlackout(true);
            setBlackoutType((prev) => prev || "SINGLE");
         }
         try {
            const monthRange = getMonthRangeYMD(selectedDate);

            let list = null;
            try {
               list = await getInstructorBlackouts(
                  resolvedInstructorId,
                  monthRange,
               );
            } catch {
               list = await getInstructorBlackouts(resolvedInstructorId);
            }

            const targetKey = selectedSlotLocalKey(selectedDate, selectedTime);

            const { foundSingle, foundRepeat } = matchBlackoutAtSlot(
               list,
               targetKey,
            );

            if (cancelled) return;

            if (foundSingle) {
               const id = blackoutIdFromRow(foundSingle);

               setHasBlackout(true);
               setBlackoutType("SINGLE");
               setBlackoutId(id);
               return;
            }

            if (foundRepeat) {
               setHasBlackout(true);
               setBlackoutType("REPEAT");
               setBlackoutId(null);
               return;
            }

            // API says free — never keep "Deblochează" from a stale calendar hint.
            setHasBlackout(false);
            setBlackoutType(null);
            setBlackoutId(null);
         } catch {
            if (cancelled) return;
            // Network error: keep calendar hint if any, else free.
            if (initialSlotIsBlocked === true) {
               setHasBlackout(true);
               setBlackoutType((prev) => prev || "SINGLE");
            } else {
               setHasBlackout(false);
               setBlackoutType(null);
               setBlackoutId(null);
            }
         } finally {
            if (!cancelled) setCheckingBlackout(false);
         }
      };

      run();
      return () => {
         cancelled = true;
      };
   }, [
      canShowBlackoutAction,
      view,
      resolvedInstructorId,
      selectedDate,
      selectedTime,
      initialSlotIsBlocked,
   ]);

   /* ====== Block / Unblock blackout SINGLE ====== */
   const handleToggleBlackout = useCallback(async () => {
      if (!resolvedInstructorId) {
         pushPill("Selectează instructorul înainte de blocare.", "error");
         return;
      }
      if (!selectedDate || !selectedTime) {
         pushPill("Lipsește ziua sau ora pentru blocare.", "error");
         return;
      }

      if (blackoutType === "REPEAT") {
         pushPill(
            "Acest slot e blocat prin REPEAT. Deblocarea se face din setările de blocări (repetitive).",
            "warning",
         );
         return;
      }

      const targetKey = selectedSlotLocalKey(selectedDate, selectedTime);
      const y = selectedDate.getFullYear();
      const mo = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const da = String(selectedDate.getDate()).padStart(2, "0");
      const dayKey = `${y}-${mo}-${da}`;
      // Same datetime as admin reservation create (local-match), not wall-clock Z.
      const slotIso = toUtcIsoFromLocal(selectedDate, selectedTime.oraStart);
      const dateTimeToSend =
         BUSY_KEYS_MODE === "local-match"
            ? isoForDbMatchLocalHour(slotIso)
            : slotIso;

      blackoutDebug("popup:toggle-blackout", {
         op: !hasBlackout ? "add" : "remove",
         targetKey,
         dayKey,
         oraStart: selectedTime.oraStart,
         dateTimeToSend,
         explain: explainBlackoutDateTime(dateTimeToSend),
         hasBlackout,
         blackoutType,
         blackoutId,
         instructorId: resolvedInstructorId,
      });

      const op = !hasBlackout ? "add" : "remove";

      const paintCalendarSlot = (paintOp) => {
         try {
            // Instant grid paint — same path as Ctrl+Q optimistic rows.
            triggerCalendarRefresh({
               source: "popup",
               type: "blackout-slot-patch",
               instructorId: String(resolvedInstructorId),
               op: paintOp,
               slotKey: targetKey,
               dateTime: dateTimeToSend,
               forceReload: false,
            });
         } catch {}
      };

      const scheduleBlackoutConfirm = () => {
         try {
            // Same delay as keyboard Ctrl+Q — immediate blackouts-changed was
            // racing the optimistic patch and wiping the cell after a few seconds.
            window.setTimeout(() => {
               try {
                  scheduleCalendarRefresh({
                     source: "popup",
                     type: "blackouts-changed",
                     instructorId: String(resolvedInstructorId),
                     forceReload: false,
                  });
               } catch {}
            }, OPTIMISTIC_BLACKOUT_REVALIDATE_DELAY_MS);
         } catch {}
      };

      setBlocking(true);
      // Paint before the network round-trip so the slot stays blocked when the
      // popup closes (keyboard shortcuts already did this).
      paintCalendarSlot(op);
      try {
         if (!hasBlackout) {
            try {
               await addInstructorBlackout(
                  Number(resolvedInstructorId),
                  dateTimeToSend,
               );
            } catch (error) {
               const text = String(error?.message || error || "").toLowerCase();
               const alreadyExists =
                  text.includes("p2002") ||
                  text.includes("unique constraint") ||
                  text.includes("instructorblackout_instructorid_datetime") ||
                  text.includes("already exists");
               if (!alreadyExists) throw error;
            }
         } else {
            const unblock = await unblockSingleBlackoutAtSlot({
               instructorId: resolvedInstructorId,
               selectedDate,
               selectedTime,
               knownBlackoutId: blackoutId,
            });
            if (!unblock?.ok) {
               throw new Error("Nu am putut debloca slotul.");
            }
         }

         scheduleBlackoutConfirm();
         closeSelf();
      } catch (e) {
         // Roll back optimistic paint on real failure.
         paintCalendarSlot(op === "add" ? "remove" : "add");
         pushPill(e?.message || "Nu am putut actualiza blocarea.", "error");
      } finally {
         setBlocking(false);
      }
   }, [
      resolvedInstructorId,
      selectedDate,
      selectedTime,
      hasBlackout,
      blackoutId,
      blackoutType,
      pushPill,
      closeSelf,
   ]);

   const blackoutButtonLabel =
      blackoutType === "REPEAT"
         ? "Blocare (repeat)"
         : hasBlackout
           ? "Deblochează"
           : "Blochează";
   const blackoutButtonIconName =
      blackoutType === "SINGLE" && hasBlackout ? "check" : "close";

   const blackoutButtonDisabled =
      blocking ||
      saving ||
      continuing ||
      checkingBlackout ||
      !resolvedInstructorId ||
      !selectedDate ||
      !selectedTime ||
      blackoutType === "REPEAT";

   const canOpenInstructorSearchView = !isInstructorSelectionLocked;
   const hasOptionsSection =
      canShowPersonalNote || canShowTicketFlags || canShowColorControl;
   const hasSelectedStudent = String(studentId || "").trim().length > 0;
   const studentFieldClassName = [
      "createReservationPopup__field",
      "createReservationPopup__field--grow-1",
      hasSelectedStudent ? "createReservationPopup__field--selected" : "",
   ]
      .filter(Boolean)
      .join(" ");
   const instructorFieldClassName = [
      "createReservationPopup__field",
      "createReservationPopup__field--grow-1",
      canOpenInstructorSearchView
         ? "createReservationPopup__field--clickable"
         : "",
   ]
      .filter(Boolean)
      .join(" ");
   const actionsRowClassName = [
      "createReservationPopup__actions-row",
      canShowBlackoutAction
         ? "createReservationPopup__actions-row--two"
         : "createReservationPopup__actions-row--single",
   ]
      .filter(Boolean)
      .join(" ");
   const isSingleInstructorMatch = filteredInstructors.length === 1;

   const renderStudentSearchContent = () => {
      const hasStudentSearchQuery = onlyDigits(phoneFull).length > 0;
      const studentMatches = hasStudentSearchQuery
         ? studentsForSearchList.slice(0, 2)
         : [];
      const isSingleStudentMatch = studentMatches.length === 1;

      return (
         <>
            <div className="createReservationPopup__new-student">
               <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
                  <div className="createReservationPopup__field createReservationPopup__field--grow-1">
                     <span className="createReservationPopup__field-label">
                        Nume elev
                     </span>
                     <input
                        className="createReservationPopup__input"
                        placeholder="Nume Prenume"
                        value={newFullName}
                        onChange={(e) => setNewFullName(e.target.value)}
                        disabled={continuing}
                     />
                  </div>

                  <div className="createReservationPopup__field createReservationPopup__field--grow-1">
                     <span className="createReservationPopup__field-label">
                        Telefon elev
                     </span>
                     <input
                        className="createReservationPopup__input"
                        placeholder="Telefon (număr)"
                        type="tel"
                        inputMode="numeric"
                        value={phoneFull}
                        onChange={(e) =>
                           setPhoneFull(onlyDigits(e.target.value))
                        }
                        onKeyDown={handlePhoneKeyDown}
                        disabled={continuing}
                        ref={phoneInputRef}
                     />
                  </div>
               </div>
            </div>
            <ul className="createReservationPopup__search-list">
               {!hasStudentSearchQuery ? (
                  <li className="createReservationPopup__search-empty">
                     Caută mai întâi elevul după telefon.
                  </li>
               ) : studentMatches.length === 0 ? (
                  <li className="createReservationPopup__search-empty">
                     Nu există potriviri.
                  </li>
               ) : (
                  studentMatches.map((s) => {
                     const full = `${s.firstName || ""} ${
                        s.lastName || ""
                     }`.trim();
                     const phone = s.phone || "";
                     const isHighlighted =
                        highlightedStudentId &&
                        String(s.id) === String(highlightedStudentId);

                     return (
                        <li
                           key={s.id}
                           className={[
                              "createReservationPopup__search-item",
                              isHighlighted
                                 ? "createReservationPopup__search-item--highlighted"
                                 : "",
                              isSingleStudentMatch
                                 ? "createReservationPopup__search-item--full"
                                 : "",
                           ]
                              .filter(Boolean)
                              .join(" ")}
                           onClick={() => {
                              if (continuing) return;
                              setStudentId(String(s.id));
                              setView("formSelect");
                              setHighlightedStudentId(null);
                              setPhoneFull("");
                              setNewFullName("");
                           }}
                        >
                           <h3>{highlightText(full, phoneFull)}</h3>
                           {phone && <p>{highlightText(phone, phoneFull)}</p>}
                        </li>
                     );
                  })
               )}
            </ul>
         </>
      );
   };

   return (
      <div ref={rootRef} className="createReservationPopup">
         <AlertPills messages={messages} onDismiss={popPill} />

         <div className="createReservationPopup__header">
            <h3 className="createReservationPopup__title">Adaugă programare</h3>
         </div>

         <div className="createReservationPopup__content">
            {view === "formSelect" && (
               <>
                  <div className="createReservationPopup__group-body">
                     {renderStudentSearchContent()}
                  </div>

                  <div className="createReservationPopup__group-body">
                     <div className="createReservationPopup__form-row createReservationPopup__form-row--spaced">
                        <div className={studentFieldClassName}>
                           <span className="createReservationPopup__field-label">
                              Elev
                           </span>

                           <div className="createReservationPopup__field-line">
                              <span className="createReservationPopup__field-text">
                                 {studentLabel}
                              </span>
                           </div>
                           <div className="createReservationPopup__field-line">
                              <span className="createReservationPopup__field-text">
                                 {studentPhone || "Telefon elev"}
                              </span>
                           </div>
                        </div>

                        <div
                           className={instructorFieldClassName}
                           onClick={() => {
                              if (continuing) return;
                              if (!canOpenInstructorSearchView) return;
                              setView("searchInstructor");
                           }}
                        >
                           <span className="createReservationPopup__field-label">
                              Instructor
                           </span>
                           <div className="createReservationPopup__field-line">
                              <span className="createReservationPopup__field-text">
                                 {instructorLabel}
                              </span>
                           </div>
                           <div className="createReservationPopup__field-line">
                              <span className="createReservationPopup__field-text">
                                 {instructorPhone ||
                                    (isInstructorSelectionLocked
                                       ? "Preluat din slot"
                                       : "Telefon instructor")}
                              </span>
                           </div>
                        </div>
                     </div>

                     <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
                        <div className="createReservationPopup__field createReservationPopup__field--grow-1">
                           <span className="createReservationPopup__field-label">
                              Ziua
                           </span>
                           <div className="createReservationPopup__field-line">
                              <span className="createReservationPopup__field-text">
                                 {dateLabel}
                              </span>
                           </div>
                        </div>

                        <div className="createReservationPopup__field createReservationPopup__field--grow-1">
                           <span className="createReservationPopup__field-label">
                              Ora
                           </span>
                           <div className="createReservationPopup__field-line">
                              <span className="createReservationPopup__field-text">
                                 {timeLabel}
                              </span>
                           </div>
                        </div>
                     </div>

                     <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
                        <div className="createReservationPopup__field createReservationPopup__field--grow-1">
                           <span className="createReservationPopup__field-label">
                              Sector
                           </span>
                           <div className="createReservationPopup__field-line">
                              <span className="createReservationPopup__field-text">
                                 {sector || "Sector nesetat"}
                              </span>
                           </div>
                        </div>

                        <div className="createReservationPopup__field createReservationPopup__field--grow-1">
                           <span className="createReservationPopup__field-label">
                              Cutie
                           </span>
                           <div className="createReservationPopup__field-line">
                              <span className="createReservationPopup__field-text">
                                 {gearbox
                                    ? gearbox.toLowerCase() === "automat"
                                       ? "Automat"
                                       : "Manual"
                                    : "Cutie nesetată"}
                              </span>
                           </div>
                        </div>
                     </div>
                  </div>

                  {hasOptionsSection ? (
                     <div className="createReservationPopup__group-body">
                        {canShowPersonalNote || canShowTicketFlags ? (
                           <div className="createReservationPopup__form-row createReservationPopup__form-row--gap">
                              {canShowPersonalNote ? (
                                 <div className="createReservationPopup__field">
                                    <span className="createReservationPopup__field-label">
                                       Notiță personală
                                    </span>
                                    <textarea
                                       className="createReservationPopup__textarea"
                                       rows={2}
                                       placeholder="Notiță pentru această rezervare (opțional)..."
                                       value={privateMessage}
                                       onChange={(e) =>
                                          setPrivateMessage(e.target.value)
                                       }
                                    />
                                 </div>
                              ) : null}

                              {canShowTicketFlags ? (
                                 <div className="createReservationPopup__field">
                                    <span className="createReservationPopup__field-label">
                                       Tichete
                                    </span>
                                    <div
                                       className="createReservationPopup__flag-grid"
                                       style={{
                                          gridTemplateColumns: "1fr 1fr",
                                       }}
                                    >
                                       <button
                                          type="button"
                                          className={[
                                             "createReservationPopup__btn",
                                             "createReservationPopup__btn--flag",
                                             isFavorite
                                                ? "createReservationPopup__btn--active"
                                                : "",
                                          ]
                                             .filter(Boolean)
                                             .join(" ")}
                                          onClick={() =>
                                             setIsFavorite((v) => !v)
                                          }
                                          title={
                                             isFavorite
                                                ? "Scoate din favorite"
                                                : "Marchează ca favorit"
                                          }
                                       >
                                          <UIIcon
                                             className="reservation-flag-icon react-icon"
                                             name="favorite"
                                          />
                                       </button>

                                       <button
                                          type="button"
                                          className={[
                                             "createReservationPopup__btn",
                                             "createReservationPopup__btn--flag",
                                             isImportant
                                                ? "createReservationPopup__btn--active"
                                                : "",
                                          ]
                                             .filter(Boolean)
                                             .join(" ")}
                                          onClick={() =>
                                             setIsImportant((v) => !v)
                                          }
                                          title={
                                             isImportant
                                                ? "Scoate marcajul de important"
                                                : "Marchează ca important"
                                          }
                                       >
                                          <UIIcon
                                             className="reservation-flag-icon react-icon"
                                             name="important"
                                          />
                                       </button>
                                    </div>
                                 </div>
                              ) : null}
                           </div>
                        ) : null}

                        {canShowColorControl ? (
                           <div className="createReservationPopup__field createReservationPopup__field--stacked createReservationPopup__field--grow-1">
                              <span className="createReservationPopup__field-label">
                                 Culoare
                              </span>

                              <div
                                 className="createReservationPopup__color-grid"
                                 role="radiogroup"
                                 aria-label="Culoare eveniment"
                                 style={COLOR_GRID_STYLE}
                              >
                                 {selectableColorTokens.map((token) => {
                                    const suffix = token.replace(/^--/, "");
                                    const active = colorToken === token;
                                    const name = COLOR_LABEL[suffix] || suffix;
                                    const tip = COLOR_HINTS[suffix]
                                       ? `${COLOR_HINTS[suffix]}`
                                       : name;

                                    return (
                                       <button
                                          key={token}
                                          type="button"
                                          role="radio"
                                          aria-checked={active}
                                          aria-label={tip}
                                          title={tip}
                                          className={[
                                             "createReservationPopup__color-swatch",
                                             `createReservationPopup__color-swatch--${suffix}`,
                                             active ? "is-active" : "",
                                          ]
                                             .filter(Boolean)
                                             .join(" ")}
                                          style={calendarEventColorSwatchStyle(token)}
                                          onClick={() => setColorToken(token)}
                                          onFocus={() => setColorHoverText(tip)}
                                          onBlur={() => setColorHoverText("")}
                                          onTouchStart={() => {
                                             setColorHoverText(tip);
                                             if (colorHoverTimerRef.current)
                                                clearTimeout(
                                                   colorHoverTimerRef.current,
                                                );
                                             colorHoverTimerRef.current =
                                                setTimeout(
                                                   () => setColorHoverText(""),
                                                   1300,
                                                );
                                          }}
                                       />
                                    );
                                 })}
                              </div>

                              {colorHoverText && (
                                 <div className="createReservationPopup__color-hint">
                                    {colorHoverText}
                                 </div>
                              )}
                           </div>
                        ) : null}
                     </div>
                  ) : null}

                  <div className="createReservationPopup__group-body">
                     <div className={actionsRowClassName}>
                        {canShowBlackoutAction ? (
                           <button
                              type="button"
                              className="createReservationPopup__btn createReservationPopup__btn--normal createReservationPopup__btn--action"
                              onClick={handleToggleBlackout}
                              disabled={blackoutButtonDisabled}
                              aria-pressed={
                                 blackoutType === "SINGLE" && hasBlackout
                              }
                              title={
                                 blackoutType === "REPEAT"
                                    ? "Blocare repetitivă (REPEAT) – se gestionează separat"
                                    : !resolvedInstructorId
                                      ? "Selectează instructorul înainte de blocare"
                                      : !selectedDate || !selectedTime
                                        ? "Lipsește ziua sau ora (vin din slotul selectat)"
                                        : ""
                              }
                           >
                              {checkingBlackout ? (
                                 "Se verifică..."
                              ) : (
                                 <>
                                    <span>{blackoutButtonLabel}</span>
                                 </>
                              )}
                           </button>
                        ) : null}

                        <button
                           onClick={onSave}
                           className="createReservationPopup__btn createReservationPopup__btn--save createReservationPopup__btn--action"
                           type="button"
                           disabled={
                              saving ||
                              continuing ||
                              !resolvedInstructorId ||
                              !canSubmitWithStudentData ||
                              !selectedDate ||
                              !selectedTime
                           }
                           title={
                              blackoutType === "REPEAT"
                                 ? "Slot blocat prin REPEAT"
                                 : blackoutType === "SINGLE" && hasBlackout
                                   ? "Slot blocat (SINGLE). Deblochează înainte."
                                   : !resolvedInstructorId
                                     ? "Selectează instructorul"
                                     : !canSubmitWithStudentData
                                       ? "Selectează elevul sau completează nume + telefon pentru elev nou"
                                       : !selectedDate || !selectedTime
                                         ? "Lipsește ziua sau ora (trebuie trimise prin props)"
                                         : ""
                           }
                        >
                           <span>
                              {continuing
                                 ? "Se creează elevul..."
                                 : saving
                                   ? "Se salvează..."
                                   : "Trimite"}
                           </span>
                        </button>
                     </div>
                  </div>
               </>
            )}

            {/* Căutare elev */}
            {/* Căutare instructor */}
            {view === "searchInstructor" && !isInstructorSelectionLocked && (
               <>
                  <div className="createReservationPopup__search-header">
                     <input
                        type="text"
                        className="createReservationPopup__search-input"
                        placeholder="Caută instructor după nume sau telefon..."
                        value={qInstructor}
                        onChange={(e) => setQInstructor(e.target.value)}
                        disabled={continuing}
                     />
                     <div className="createReservationPopup__search-header-actions">
                        <button
                           className="createReservationPopup__btn createReservationPopup__btn--normal"
                           type="button"
                           onClick={() => setView("formSelect")}
                           disabled={continuing}
                        >
                           Înapoi
                        </button>
                     </div>
                  </div>

                  <ul className="createReservationPopup__search-list">
                     {filteredInstructors.map((i) => {
                        const full = `${i.firstName || ""} ${
                           i.lastName || ""
                        }`.trim();
                        const phone = i.phone || "";

                        return (
                           <li
                              key={i.id}
                              className={[
                                 "createReservationPopup__search-item",
                                 isSingleInstructorMatch
                                    ? "createReservationPopup__search-item--full"
                                    : "",
                              ]
                                 .filter(Boolean)
                                 .join(" ")}
                              onClick={() => {
                                 if (continuing) return;
                                 const nextInstructorId =
                                    normalizeInstructorIdForCreate(i.id);
                                 const nextSector =
                                    resolveSectorFromInstructorForCreate({
                                       instructorId: nextInstructorId,
                                       instructor: i,
                                       instructorsGroups,
                                       fallback: "",
                                    });
                                 const nextGearbox =
                                    resolveGearboxFromInstructorForCreate({
                                       instructorId: nextInstructorId,
                                       instructor: i,
                                       cars,
                                       fallback: "",
                                    });

                                 setInstructorId(String(i.id));
                                 setSector(nextSector);
                                 setGearbox(nextGearbox);
                                 setView("formSelect");
                              }}
                           >
                              <h3>{highlightText(full, qInstructor)}</h3>
                              {phone && (
                                 <p>{highlightText(phone, qInstructor)}</p>
                              )}
                           </li>
                        );
                     })}
                  </ul>
               </>
            )}
         </div>
      </div>
   );
}
